import {
  DISTRIBUTION_CHANNELS,
  DistributionChannel,
  getProvinceName,
  PROVINCE_NAMES,
  ProvinceName,
  YES_NO_FLAG,
} from '@repo/constants'
import { z } from 'zod'

// CSV boolean schema accepting Y/N in both uppercase and lowercase
export const csvBooleanSchema = z.enum([
  YES_NO_FLAG.YES,
  YES_NO_FLAG.NO,
  YES_NO_FLAG.YES.toLowerCase(),
  YES_NO_FLAG.NO.toLowerCase(),
])
// CSV boolean schema accepting X (active) or empty string (inactive), coerced to boolean
export const csvXEmptyBooleanSchema = z
  .enum(['X', 'x', ''])
  .transform((val) => val.toUpperCase() === 'X')

export const csvMaterialSchema = z.union([z.string(), z.number()])
export const csvErpSchema = z.union([z.string(), z.number()])

export const parsedNumberSchema = z
  .string()
  .transform((val) => val.replace(/,/g, ''))
  .pipe(z.coerce.number())

/**
 * A numeric cell the way a spreadsheet exports it: thousands separators dropped, and an accounting
 * negative such as `(176.06)` read as `-176.06`. Returns the cleaned text so callers that store the
 * cell as a string (cost of goods) and callers that coerce it (the zod schemas) share one rule.
 */
export function normalizeAccountingNumber(value: string): string {
  const cleaned = value.replace(/,/g, '').trim()
  const accounting = /^\((.*)\)$/.exec(cleaned)?.[1]
  return accounting === undefined ? cleaned : `-${accounting.trim()}`
}

/** `parsedNumberSchema` that also accepts the accounting negative, for the hand-made CRT imports. */
export const accountingNumberSchema = z
  .string()
  .transform(normalizeAccountingNumber)
  .pipe(z.coerce.number())

export const trimLeadingZerosSchema = z
  .string()
  .transform((value) => value.replace(/^[\s0]+/, ''))
  .pipe(z.string().trim())

const PROVINCE_NAME_LOWERCASE_TO_NORMALIZED = Object.fromEntries(
  PROVINCE_NAMES.map((name) => [name.toLowerCase(), name]),
) as Record<string, ProvinceName>

function normalizeProvinceName(provinceName: string): ProvinceName | null {
  const normalized = provinceName.trim().toLowerCase()

  // Special handling for common variations
  if (normalized.includes('newfoundland')) {
    return getProvinceName('NL')
  } else if (normalized === 'yukon territory') {
    return getProvinceName('YT')
  } else if (normalized === 'northwest terr.' || normalized.includes('northwest terr')) {
    return getProvinceName('NT')
  }

  return PROVINCE_NAME_LOWERCASE_TO_NORMALIZED[normalized] ?? null
}

export const provinceNameSchema = z.coerce.string().transform((val) => normalizeProvinceName(val))

const DISTRIBUTION_CHANNEL_LOWERCASE_TO_NORMALIZED = Object.fromEntries(
  DISTRIBUTION_CHANNELS.map((name) => [name.toLowerCase(), name]),
) as Record<string, DistributionChannel>

function normalizeDistributionChannel(distributionChannel: string): DistributionChannel | null {
  return (
    DISTRIBUTION_CHANNEL_LOWERCASE_TO_NORMALIZED[distributionChannel.trim().toLowerCase()] ?? null
  )
}

export const distributionChannelNameSchema = z.coerce
  .string()
  .transform((val) => normalizeDistributionChannel(val))
